"use client"

import { Loader2, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useDeleteKnowledgeBaseFolderMutation } from "@/hooks/mutations/knowledge-base-folders"
import type {
  DeleteKnowledgeBaseFolderActionResult,
  KnowledgeBaseFolderView,
} from "@/server-actions/knowledge-base-folders"
import { useTranslations } from "next-intl"


type DeleteFolderDialogProps = {
  folder: KnowledgeBaseFolderView | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted?: (
    result: DeleteKnowledgeBaseFolderActionResult,
  ) => void | Promise<void>
}

export function DeleteFolderDialog({
  folder,
  open,
  onOpenChange,
  onDeleted,
}: DeleteFolderDialogProps) {
  const tUi = useTranslations("Ai.AiKnowledgeBaseDeleteFolderDialog")
  const tCommonUi = useTranslations("Common")

  const deleteFolder = useDeleteKnowledgeBaseFolderMutation()

  const handleDelete = async () => {
    if (!folder) return

    try {
      const result = await deleteFolder.mutateAsync(folder.id)
      onOpenChange(false)
      void Promise.resolve(onDeleted?.(result)).catch((error) => {
        console.error("[Knowledge Base] Failed to refresh after deleting folder:", error)
      })
    } catch {
      // withQueryHandling displays the server error and keeps the dialog open.
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><span><span key={tUi("delete")}>{tUi("delete")}</span></span> {folder?.name ?? tUi("folder")}<span>?</span></AlertDialogTitle>
          <AlertDialogDescription>
            <span><span key={tUi("thisPermanentlyDeletesTheFolderAndAll")}>{tUi("thisPermanentlyDeletesTheFolderAndAll")}</span></span> {folder?.document_count ?? 0} <span><span key={tUi("documentsInsideItTheirEmbeddingsStoredFilesElevenLabs")}>{tUi("documentsInsideItTheirEmbeddingsStoredFilesElevenLabs")}</span></span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteFolder.isPending}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!folder || deleteFolder.isPending}
          >
            {deleteFolder.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            <span><span key={tUi("deleteFolder")}>{tUi("deleteFolder")}</span></span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
